export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const EVENT_TYPE_META = {
  vilt: {
    label: 'VILT Session',
    chipClass: 'bg-indigo-500 text-white border-indigo-600',
    dotClass: 'bg-indigo-500',
    badgeClass: 'bg-indigo-100 text-indigo-800'
  },
  teams: {
    label: 'Microsoft Teams',
    chipClass: 'bg-[#6264A7] text-white border-[#4f52a0]',
    dotClass: 'bg-[#6264A7]',
    badgeClass: 'bg-purple-100 text-purple-800'
  },
  zoom: {
    label: 'Zoom Meeting',
    chipClass: 'bg-[#2D8CFF] text-white border-[#1b6fd6]',
    dotClass: 'bg-[#2D8CFF]',
    badgeClass: 'bg-blue-100 text-blue-800'
  },
  google_meet: {
    label: 'Google Meet',
    chipClass: 'bg-[#34A853] text-white border-[#2d8f47]',
    dotClass: 'bg-[#34A853]',
    badgeClass: 'bg-emerald-100 text-emerald-800'
  },
  meeting: {
    label: 'Live Meeting',
    chipClass: 'bg-sky-500 text-white border-sky-600',
    dotClass: 'bg-sky-500',
    badgeClass: 'bg-sky-100 text-sky-800'
  },
  assessment: {
    label: 'Assessment',
    chipClass: 'bg-lime-500 text-white border-lime-600',
    dotClass: 'bg-lime-500',
    badgeClass: 'bg-lime-100 text-lime-800'
  },
  course: {
    label: 'Scheduled Course',
    chipClass: 'bg-violet-500 text-white border-violet-600',
    dotClass: 'bg-violet-500',
    badgeClass: 'bg-violet-100 text-violet-800'
  }
};

export const getEventMeta = (event) => {
  if (event.type === 'meeting' && event.meetingPlatform && EVENT_TYPE_META[event.meetingPlatform]) {
    return EVENT_TYPE_META[event.meetingPlatform];
  }
  return EVENT_TYPE_META[event.type] || EVENT_TYPE_META.meeting;
};

export const getMonthRange = (year, month) => {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
  return { start, end };
};

export const buildMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

export const isSameDay = (left, right) => {
  if (!left || !right) return false;
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
};

export const formatMonthYear = (date) => (
  date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
);

/**
 * Parse calendar event dates without UTC day-shifts.
 * - YYYY-MM-DD → local midnight
 * - YYYY-MM-DDTHH:mm:ss (no Z) → local wall clock
 */
export const parseCalendarDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split('-').map(Number);
      return new Date(y, m - 1, d);
    }

    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)
      && !trimmed.endsWith('Z')
      && !/[+-]\d{2}:\d{2}$/.test(trimmed)
    ) {
      const [datePart, timePart] = trimmed.split('T');
      const [y, m, d] = datePart.split('-').map(Number);
      const [hh = 0, mm = 0, ss = 0] = timePart.split(/[:.]/).map((part) => parseInt(part, 10) || 0);
      return new Date(y, m - 1, d, hh, mm, ss);
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatEventTime = (event) => {
  if (event.allDay) {
    return 'All day';
  }
  const start = parseCalendarDate(event.start);
  const end = event.end ? parseCalendarDate(event.end) : null;
  if (!start) return '';
  const startText = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (!end) return startText;
  const endText = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${startText} – ${endText}`;
};

export const formatEventDate = (value) => {
  const date = parseCalendarDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const getEventsForDay = (events, day) => {
  if (!day) return [];
  const dayKey = toDateKey(day);

  return events.filter((event) => {
    if (event.allDay) {
      const eventStart = parseCalendarDate(event.start);
      return Boolean(eventStart) && toDateKey(eventStart) === dayKey;
    }

    const eventStart = parseCalendarDate(event.start);
    const eventEnd = parseCalendarDate(event.end || event.start);
    if (!eventStart) return false;

    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999);
    const end = eventEnd || eventStart;
    return end >= dayStart && eventStart <= dayEnd;
  });
};

export const toDateKey = (date) => {
  if (!date) return '';
  const parsed = date instanceof Date ? date : parseCalendarDate(date);
  if (!parsed) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
